import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiDelete } from '../services/api';
import './FichajePage.css';

function pad(n) { return String(n).padStart(2, '0'); }

// Convierte string de BD (UTC) a Date correcta añadiendo 'Z' si no lo tiene
function toDate(str) {
  if (!str) return null;
  if (str instanceof Date) return str;
  let s = str.replace(' ', 'T');
  if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
  return new Date(s);
}

function fmtTime(str) {
  const d = toDate(str);
  if (!d) return '—';
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(str) {
  const d = toDate(str);
  if (!d) return '—';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function duracion(entrada, salida) {
  if (!entrada || !salida) return null;
  const diff = Math.max(0, toDate(salida) - toDate(entrada));
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  return `${pad(h)}h ${pad(m)}m ${pad(sec)}s`;
}

// Obtiene coordenadas del navegador
function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocalización no disponible'));
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });
  });
}

function geoErrorMessage(err) {
  if (!err) return 'Error de geolocalización';
  switch (err.code) {
    case 1: return 'Permiso denegado: habilita la ubicación para este sitio';
    case 2: return 'GPS no disponible: activa la ubicación del dispositivo';
    case 3: return 'Tiempo agotado buscando GPS (sal al exterior o reintenta)';
    default: return err.message || 'Error de geolocalización';
  }
}

// Agrupa fichajes en pares [entrada, salida] respetando el orden temporal real.
// Algoritmo: recorre los eventos en orden cronológico; cada entrada abre un par
// nuevo, cada salida cierra el par abierto (o crea fila huérfana sin entrada).
// Una entrada que llega con un par ya abierto cierra el anterior sin salida.
function buildPares(fichajes) {
  // Agrupar por usuario + día
  const byUserDay = {};
  for (const f of fichajes) {
    const day = (f.fecha_hora || '').slice(0, 10);
    if (!day) continue;
    const key = `${f.user_id}__${day}`;
    if (!byUserDay[key]) byUserDay[key] = { user_id: f.user_id, user_name: f.user_name, day, events: [] };
    byUserDay[key].events.push(f);
  }

  const rows = [];
  function emptyRow(user_id, user_name, day) {
    return {
      user_id, user_name, day,
      id_e: null, id_s: null,
      entrada: null, salida: null,
      addr_entrada: null, addr_salida: null,
      lat_e: undefined, lon_e: undefined,
      lat_s: undefined, lon_s: undefined,
      duracion: null,
    };
  }
  function closePair(pair) {
    if (pair.entrada && pair.salida) pair.duracion = duracion(pair.entrada, pair.salida);
    rows.push(pair);
  }

  for (const { user_id, user_name, day, events } of Object.values(byUserDay)) {
    events.sort((a, b) => a.fecha_hora.localeCompare(b.fecha_hora));
    let open = null;
    for (const ev of events) {
      if (ev.tipo === 'entrada') {
        // Si ya hay un par abierto sin cerrar, lo cerramos como huérfano (entrada sin salida)
        if (open) closePair(open);
        open = emptyRow(user_id, user_name, day);
        open.id_e = ev.id;
        open.entrada = ev.fecha_hora;
        open.addr_entrada = ev.address || null;
        open.lat_e = ev.latitude;
        open.lon_e = ev.longitude;
      } else { // salida
        if (open) {
          open.id_s = ev.id;
          open.salida = ev.fecha_hora;
          open.addr_salida = ev.address || null;
          open.lat_s = ev.latitude;
          open.lon_s = ev.longitude;
          closePair(open);
          open = null;
        } else {
          // Salida sin par abierto → fila huérfana (sin entrada)
          const orphan = emptyRow(user_id, user_name, day);
          orphan.id_s = ev.id;
          orphan.salida = ev.fecha_hora;
          orphan.addr_salida = ev.address || null;
          orphan.lat_s = ev.latitude;
          orphan.lon_s = ev.longitude;
          rows.push(orphan);
        }
      }
    }
    // Último par sin salida → "En curso"
    if (open) closePair(open);
  }

  // Ordenar por día desc; dentro del día, por timestamp del evento más reciente desc
  rows.sort((a, b) => {
    if (b.day !== a.day) return b.day.localeCompare(a.day);
    const aRef = a.entrada || a.salida || '';
    const bRef = b.entrada || b.salida || '';
    return bRef.localeCompare(aRef);
  });
  return rows;
}

function MapLink({ lat, lon, children }) {
  if (!lat || !lon) return <span className="fj-no-geo">Sin ubicación</span>;
  return (
    <a
      href={`https://www.google.com/maps?q=${lat},${lon}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fj-map-link"
      title="Ver en Google Maps"
    >
      📍 {children}
    </a>
  );
}

// ── Vista del COMERCIAL ────────────────────────────────────────────────────────
function VistaComercial({ token }) {
  const [ultimo,      setUltimo]      = useState(null);
  const [ultimaFH,   setUltimaFH]   = useState(null);
  const [fichajes,   setFichajes]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fichando,   setFichando]   = useState(false);
  const [geoStatus,  setGeoStatus]  = useState('');
  const [timer,      setTimer]      = useState('');
  const [monthFilter, setMonthFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  });

  const loadEstado = useCallback(async () => {
    const d = await apiGet('/fichajes/estado', token);
    setUltimo(d.ultimo);
    setUltimaFH(d.fecha_hora);
  }, [token]);

  const loadFichajes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/fichajes?month=${monthFilter}`, token);
      setFichajes(data);
    } finally { setLoading(false); }
  }, [token, monthFilter]);

  useEffect(() => { loadEstado(); }, [loadEstado]);
  useEffect(() => { loadFichajes(); }, [loadFichajes]);

  useEffect(() => {
    if (ultimo !== 'entrada' || !ultimaFH) { setTimer(''); return; }
    const base = toDate(ultimaFH);
    const tick = () => {
      const diff = Math.max(0, Date.now() - base.getTime());
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimer(`${pad(h)}:${pad(m)}:${pad(s)}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [ultimo, ultimaFH]);

  async function fichar(tipo) {
    setFichando(true);
    setGeoStatus('Obteniendo ubicación…');
    let latitude = null, longitude = null;
    try {
      const pos = await getPosition();
      latitude  = pos.coords.latitude;
      longitude = pos.coords.longitude;
      setGeoStatus('');
    } catch (err) {
      setGeoStatus(geoErrorMessage(err));
      setTimeout(() => setGeoStatus(''), 8000);
    }
    try {
      await apiPost('/fichajes', { tipo, latitude, longitude }, token);
    } catch (e) {
      setGeoStatus('Error al fichar: ' + (e.message || 'Error desconocido'));
      setTimeout(() => setGeoStatus(''), 8000);
      setFichando(false);
      return;
    }
    // Refresco estado tras fichar correctamente
    try { await loadEstado(); } catch {}
    try { await loadFichajes(); } catch {}
    setFichando(false);
  }

  const trabajando = ultimo === 'entrada';
  const pares = buildPares(fichajes);

  return (
    <div className="fj-page">
      <div className={`fj-clock-card ${trabajando ? 'fj-clock-card--in' : 'fj-clock-card--out'}`}>
        <div className="fj-clock-status">
          <span className={`fj-status-dot ${trabajando ? 'fj-status-dot--in' : 'fj-status-dot--out'}`} />
          <span className="fj-status-label">{trabajando ? 'Trabajando' : 'Fuera del trabajo'}</span>
        </div>
        {trabajando && timer && <div className="fj-timer">{timer}</div>}
        {trabajando && ultimaFH && (
          <div className="fj-since">Entrada registrada a las {fmtTime(ultimaFH)}</div>
        )}
        {geoStatus && <div className="fj-geo-status">{geoStatus}</div>}
        <button
          className={`fj-btn-fichar ${trabajando ? 'fj-btn-fichar--salida' : 'fj-btn-fichar--entrada'}`}
          onClick={() => fichar(trabajando ? 'salida' : 'entrada')}
          disabled={fichando}
        >
          {fichando ? 'Registrando…' : trabajando ? '⏹ Registrar Salida' : '▶ Registrar Entrada'}
        </button>
        <div className="fj-geo-note">Se registrará tu ubicación al fichar</div>
      </div>

      <div className="fj-section">
        <div className="fj-section-header">
          <h3 className="fj-section-title">Historial de fichajes</h3>
          <input type="month" className="fj-month-input" value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)} />
        </div>
        {loading ? (
          <div className="fj-empty">Cargando…</div>
        ) : pares.length === 0 ? (
          <div className="fj-empty">No hay fichajes en este período</div>
        ) : (
          <table className="fj-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Entrada</th>
                <th>Ubicación entrada</th>
                <th>Salida</th>
                <th>Ubicación salida</th>
                <th>Duración</th>
              </tr>
            </thead>
            <tbody>
              {pares.map((p, i) => (
                <tr key={i}>
                  <td className="fj-td-date">{fmtDate(p.day + 'T12:00:00Z')}</td>
                  <td className="fj-td-time fj-td-time--in">{p.entrada ? fmtTime(p.entrada) : '—'}</td>
                  <td className="fj-td-addr">
                    <MapLink lat={p.lat_e} lon={p.lon_e}>
                      {p.addr_entrada ? <span className="fj-addr-text" title={p.addr_entrada}>{p.addr_entrada}</span> : 'Ver mapa'}
                    </MapLink>
                  </td>
                  <td className="fj-td-time fj-td-time--out">{p.salida ? fmtTime(p.salida) : <span className="fj-badge-activo">En curso</span>}</td>
                  <td className="fj-td-addr">
                    {p.salida
                      ? <MapLink lat={p.lat_s} lon={p.lon_s}>
                          {p.addr_salida ? <span className="fj-addr-text" title={p.addr_salida}>{p.addr_salida}</span> : 'Ver mapa'}
                        </MapLink>
                      : '—'}
                  </td>
                  <td className="fj-td-dur">{p.duracion || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Vista del ADMIN ────────────────────────────────────────────────────────────
function VistaAdmin({ token }) {
  const [fichajes,    setFichajes]    = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterMode,  setFilterMode]  = useState('month');
  const [monthFilter, setMonthFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  });
  const [dayFilter, setDayFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [comercialFilter, setComercialFilter] = useState('todos');

  // Carga lista de comerciales al montar
  useEffect(() => {
    apiGet('/fichajes/comerciales', token)
      .then(data => setComerciales(data))
      .catch(() => {});
  }, [token]);

  const loadFichajes = useCallback(async () => {
    setLoading(true);
    try {
      const q = filterMode === 'month' ? `month=${monthFilter}` : `date=${dayFilter}`;
      const userQ = comercialFilter !== 'todos' ? `&user_id=${comercialFilter}` : '';
      const data = await apiGet(`/fichajes?${q}${userQ}`, token);
      setFichajes(data);
    } finally { setLoading(false); }
  }, [token, filterMode, monthFilter, dayFilter, comercialFilter]);

  useEffect(() => { loadFichajes(); }, [loadFichajes]);

  async function handleDelete(id, descripcion) {
    if (!id) return;
    if (!window.confirm(`¿Eliminar el registro de ${descripcion}? Esta acción no se puede deshacer.`)) return;
    try {
      await apiDelete(`/fichajes/${id}`, token);
      await loadFichajes();
    } catch (e) {
      window.alert('Error al eliminar: ' + (e.message || 'Error desconocido'));
    }
  }

  const pares = buildPares(fichajes);

  const totalesPorUser = {};
  pares.forEach(p => {
    const uid = p.user_id;
    if (!totalesPorUser[uid]) totalesPorUser[uid] = { name: p.user_name, ms: 0 };
    if (p.entrada && p.salida) {
      const e = new Date(p.entrada.replace(' ', 'T'));
      const s = new Date(p.salida.replace(' ', 'T'));
      totalesPorUser[uid].ms += Math.max(0, s - e);
    }
  });

  function msToHM(ms) {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${pad(m)}m`;
  }

  return (
    <div className="fj-page">
      <div className="fj-filter-bar">
        <div className="fj-filter-tabs">
          <button className={`fj-filter-tab ${filterMode === 'month' ? 'active' : ''}`}
            onClick={() => setFilterMode('month')}>Por mes</button>
          <button className={`fj-filter-tab ${filterMode === 'day' ? 'active' : ''}`}
            onClick={() => setFilterMode('day')}>Por día</button>
        </div>
        {filterMode === 'month'
          ? <input type="month" className="fj-month-input" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} />
          : <input type="date"  className="fj-month-input" value={dayFilter}   onChange={e => setDayFilter(e.target.value)} />
        }
        <select className="fj-month-input" value={comercialFilter} onChange={e => setComercialFilter(e.target.value)}>
          <option value="todos">Todos los comerciales</option>
          {comerciales.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {Object.keys(totalesPorUser).length > 0 && (
        <div className="fj-summary-cards">
          {Object.values(totalesPorUser).map((u, i) => (
            <div key={i} className="fj-summary-card">
              <div className="fj-summary-name">{u.name}</div>
              <div className="fj-summary-hours">{msToHM(u.ms)}</div>
              <div className="fj-summary-label">horas totales</div>
            </div>
          ))}
        </div>
      )}

      <div className="fj-section">
        <h3 className="fj-section-title">Fichajes de comerciales</h3>
        {loading ? (
          <div className="fj-empty">Cargando…</div>
        ) : pares.length === 0 ? (
          <div className="fj-empty">No hay fichajes en este período</div>
        ) : (
          <table className="fj-table fj-table--admin">
            <thead>
              <tr>
                <th>Comercial</th>
                <th>Fecha</th>
                <th>Entrada</th>
                <th>Ubicación entrada</th>
                <th>Salida</th>
                <th>Ubicación salida</th>
                <th>Duración</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pares.map((p, i) => (
                <tr key={i}>
                  <td className="fj-td-user">{p.user_name}</td>
                  <td className="fj-td-date">{fmtDate(p.day + 'T12:00:00Z')}</td>
                  <td className="fj-td-time fj-td-time--in">{p.entrada ? fmtTime(p.entrada) : '—'}</td>
                  <td className="fj-td-addr">
                    <MapLink lat={p.lat_e} lon={p.lon_e}>
                      {p.addr_entrada
                        ? <span className="fj-addr-text" title={p.addr_entrada}>{p.addr_entrada}</span>
                        : 'Ver mapa'}
                    </MapLink>
                  </td>
                  <td className="fj-td-time fj-td-time--out">
                    {p.salida ? fmtTime(p.salida) : <span className="fj-badge-activo">En curso</span>}
                  </td>
                  <td className="fj-td-addr">
                    {p.salida
                      ? <MapLink lat={p.lat_s} lon={p.lon_s}>
                          {p.addr_salida
                            ? <span className="fj-addr-text" title={p.addr_salida}>{p.addr_salida}</span>
                            : 'Ver mapa'}
                        </MapLink>
                      : '—'}
                  </td>
                  <td className="fj-td-dur">{p.duracion || '—'}</td>
                  <td className="fj-td-actions">
                    {p.id_e && (
                      <button
                        type="button"
                        className="fj-btn-delete"
                        title={`Eliminar entrada de ${p.user_name} (${fmtTime(p.entrada)})`}
                        onClick={() => handleDelete(p.id_e, `entrada de ${p.user_name} a las ${fmtTime(p.entrada)}`)}
                      >
                        🗑 Entrada
                      </button>
                    )}
                    {p.id_s && (
                      <button
                        type="button"
                        className="fj-btn-delete"
                        title={`Eliminar salida de ${p.user_name} (${fmtTime(p.salida)})`}
                        onClick={() => handleDelete(p.id_s, `salida de ${p.user_name} a las ${fmtTime(p.salida)}`)}
                      >
                        🗑 Salida
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function FichajePage() {
  const { auth } = useContext(AuthContext);
  const role = auth.user?.role;
  if (role === 'comercial') return <VistaComercial token={auth.token} />;
  if (['admin', 'superadmin'].includes(role)) return <VistaAdmin token={auth.token} />;
  return null;
}
